import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, ChangeDetectionStrategy } from '@angular/core';
import { Location } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl } from '@angular/forms';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';

import { Subject, Observable, of } from 'rxjs';
import { map, merge, take } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';
import { fadeInOut } from '../../../services/animations';
import { StringStringTuple, ReportMetadataService, OrganizationViewModel, OrganizationService, Int32StringTuple } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { CanComponentDeactivate } from '../../../services/deactivate-guard';

@Component({
    selector: 'app-organization-details',
    templateUrl: './organization-details.component.html',
    styleUrls: ['./organization-details.component.scss'],
    animations: [fadeInOut],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OrganizationDetailsComponent implements OnInit, OnChanges, CanComponentDeactivate {
    @ViewChild('form', { static: true }) form: NgForm;
    @ViewChild('app-business-list', { static: true })

    organization: OrganizationViewModel;
    users$: Observable<Array<StringStringTuple>>;
    regions$: Observable<Int32StringTuple[]>;
    createdByName$: Observable<string>;
    changedByName$: Observable<string>;

    id: number;
    organizationForm: UntypedFormGroup;
    private onRecordSaved = new Subject<OrganizationViewModel>();
    reportSaved$ = this.onRecordSaved.asObservable();

    public isNew = false;
    private isSaving = false;

    constructor(
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private organizationService: OrganizationService,
        private reportMetadataService: ReportMetadataService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private snackBar: MatSnackBar,
        private authService: AuthService
    ) {
        this.id = Number.parseInt(this.route.snapshot.paramMap.get('id'));
    }

    ngOnInit(): void {
        this.buildForm();

        this.users$ = this.reportMetadataService.getUserMetadata();
        this.regions$ = this.reportMetadataService.getRegionMetadata();

        if (this.id) {
            this.organizationService.getOrganizationById(this.id).subscribe(ok => this.dataLoaded(ok), error => this.alertService.showStickyMessage(error));
        } else {
            this.isNew = true;
            this.organization = this.initializeEmptyObject();
            this.resetForm();
        }
    }

    ngOnChanges(): void {
        if (this.organization) {
            this.isNew = false;
        }
        else {
            this.isNew = true;
            this.organization = this.initializeEmptyObject();
        }

        this.resetForm();
    }


    dataLoaded(organization: OrganizationViewModel) {
        this.organization = organization;

        this.isNew = false;
        this.resetForm();
    }

    public getFormControl(name: string): AbstractControl {
        return this.organizationForm.controls[name];
    }


    private buildForm() {
        this.organizationForm = this.formBuilder.group({
            organizationId: '',
            name: ['', Validators.compose([Validators.required, Validators.maxLength(100)])],
            regionId: '',
            enabled: '',
            email: ['', Validators.maxLength(255)],
            phone: ['', Validators.maxLength(50)],
            organizationNo: ['', Validators.maxLength(20)],
            contactPerson: ['', Validators.maxLength(200)],
            ownedBy: '',
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
        });
    }

    public resetForm() {
        if (!this.organization) {
            this.isNew = true;
            this.organization = this.initializeEmptyObject();
        }

        if (this.isNew) {
            if (!this.organization.ownedBy) {
                this.organization.ownedBy = this.authService.currentUser.id;
            }
        }

        this.organizationForm.reset({
            organizationId: this.organization.organizationId || '',
            name: this.organization.name || '',
            regionId: this.organization.regionId || '',
            enabled: this.organization.enabled || '',
            email: this.organization.email || '',
            phone: this.organization.phone || '',
            organizationNo: this.organization.organizationNo || '',
            contactPerson: this.organization.contactPerson ||'',
            ownedBy: this.organization.ownedBy || '',
            createdBy: this.organization.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.organization.createdDate, true) || '',
            updatedBy: this.organization.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.organization.updatedDate, true) || '',
        });

        if (this.organization.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.organization.createdBy).pipe(map(t => t.item2));
        }
        if (this.organization.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.organization.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): OrganizationViewModel {
        var result = <OrganizationViewModel>{};
        result.enabled = true;

        return result;
    }

    private prepareSave(): OrganizationViewModel {
        const formModel = this.organizationForm.value;

        return {
            organizationId: this.isNew ? null : formModel.organizationId,
            name: formModel.name,
            regionId: formModel.regionId,
            enabled: formModel.enabled ? true : false,
            email: formModel.email,
            phone: formModel.phone,
            contactPerson: formModel.contactPerson,
            organizationNo: formModel.organizationNo,
            ownedBy: formModel.ownedBy,
        };
    }

    public save() {
        if (!this.organizationForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const organization = this.prepareSave();

        if (this.isNew) {
            this.organizationService.createOrganization(organization).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
        else {
            this.organizationService.updateOrganization(organization.organizationId, organization).subscribe(
                t => this.saveCompleted(t),
                error => this.saveFailed(error));
        }
    }

    public cancel() {
        this.location.back();
    }

    // Prevent leaving the form accidentaly if the form is dirty
    canDeactivate(): Observable<boolean> {
        this.alertService.resetStickyMessage();

        if (this.organizationForm.dirty) {
            let snackbar = this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 });

            let close = snackbar.onAction().pipe(map(() => { return true; }));
            let remain = snackbar.afterDismissed().pipe(map(() => { return false; }));

            return close.pipe(
                merge(remain),
                take(1));

        } else {
            return of(true);
        }
    }


    private saveCompleted(organization: OrganizationViewModel) {
        this.organization = organization;
        this.id = organization.organizationId;
        this.isNew = false;

        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), organization.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        this.resetForm();

        this.onRecordSaved.next(this.organization);

    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.changeOrganizations);
    }

}
